import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Menu, X, Bell, LogOut, Languages, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { MyMissingDataBanner } from "@/pages/admin/AdminWidgets";

const NAV_BY_ROLE = {
  inspector: [
    { path: "/inspector/route", key: "nav_todays_route" },
    { path: "/inspector/escalations", key: "nav_my_escalations" },
    { path: "/inspector/scorecard", key: "nav_my_scorecard" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/help", key: "nav_help" },
  ],
  supervisor: [
    { path: "/supervisor/assignment-queue", key: "nav_assignment_queue" },
    { path: "/supervisor/live-board", key: "nav_live_board" },
    { path: "/supervisor/escalations", key: "nav_escalations_inbox" },
    { path: "/supervisor/route-planner", key: "nav_route_planner" },
    { path: "/supervisor/grade-review", key: "nav_grade_review" },
    { path: "/supervisor/audit", key: "nav_audit_mode" },
    { path: "/supervisor/audit-mismatches", key: "nav_audit_mismatches" },
    { path: "/supervisor/device-sync", key: "nav_device_sync" },
    { path: "/supervisor/missing-etas", key: "nav_missing_etas" },
    { path: "/supervisor/unallocated", key: "nav_unallocated" },
    { path: "/factory/vendor-followup", key: "nav_vendor_followup" },
    { path: "/hr/attendance", key: "nav_hr_attendance" },
    { path: "/hr/attendance/month", key: "nav_hr_month" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/gate/register", key: "nav_gate_register" },
    { path: "/admin/import-health", key: "nav_import_health" },
    { path: "/admin/csv-upload", key: "nav_csv_upload" },
    { path: "/help", key: "nav_help" },
  ],
  coordinator: [
    { path: "/coordinator/master-data", key: "nav_master_data" },
    { path: "/coordinator/weekly-mis", key: "nav_weekly_mis" },
    { path: "/coordinator/po-register", key: "nav_po_register" },
    { path: "/supervisor/audit-mismatches", key: "nav_audit_mismatches" },
    { path: "/factory/wip", key: "nav_factory_wip" },
    { path: "/factory/stock", key: "nav_stock_on_hand" },
    { path: "/factory/vendor-followup", key: "nav_vendor_followup" },
    { path: "/coordinator/job-work", key: "nav_jw_register" },
    { path: "/coordinator/job-workers", key: "nav_jw_workers" },
    { path: "/gate/register", key: "nav_gate_register" },
    { path: "/hr/staff", key: "nav_hr_staff" },
    { path: "/hr/attendance", key: "nav_hr_attendance" },
    { path: "/hr/attendance/month", key: "nav_hr_month" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/director/import", key: "nav_import" },
    { path: "/admin/import-health", key: "nav_import_health" },
    { path: "/admin/csv-upload", key: "nav_csv_upload" },
    { path: "/admin/refresh", key: "nav_refresh" },
    { path: "/help", key: "nav_help" },
  ],
  merchandiser: [
    { path: "/merch/new-po", key: "nav_new_po" },
    { path: "/merch/my-pos", key: "nav_my_pos" },
    { path: "/admin/csv-upload", key: "nav_csv_upload" },
    { path: "/help", key: "nav_help" },
  ],
  director: [
    { path: "/director/dashboard", key: "nav_dashboard" },
    { path: "/supervisor/live-board", key: "nav_live_board" },
    { path: "/coordinator/po-register", key: "nav_po_register" },
    { path: "/coordinator/weekly-mis", key: "nav_weekly_mis" },
    { path: "/supervisor/audit-mismatches", key: "nav_audit_mismatches" },
    { path: "/supervisor/device-sync", key: "nav_device_sync" },
    { path: "/factory/wip", key: "nav_factory_wip" },
    { path: "/factory/finishing", key: "nav_finishing" },
    { path: "/factory/stock", key: "nav_stock_on_hand" },
    { path: "/factory/despatch", key: "nav_despatch" },
    { path: "/factory/grn", key: "nav_grn" },
    { path: "/factory/vendor-followup", key: "nav_vendor_followup" },
    { path: "/coordinator/job-work", key: "nav_jw_register" },
    { path: "/gate/register", key: "nav_gate_register" },
    { path: "/hr/attendance/month", key: "nav_hr_month" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/director/portal-tokens", key: "nav_portal_tokens" },
    { path: "/director/ric-trade", key: "nav_ric_trade" },
    { path: "/director/import", key: "nav_import" },
    { path: "/admin/import-health", key: "nav_import_health" },
    { path: "/admin/csv-upload", key: "nav_csv_upload" },
    { path: "/admin/kra-leaderboard", key: "nav_kra_leaderboard" },
    { path: "/admin/refresh", key: "nav_refresh" },
    { path: "/admin/maintenance", key: "nav_maintenance" },
    { path: "/help", key: "nav_help" },
  ],
  prod_qc_coordinator: [
    { path: "/factory/wip", key: "nav_factory_wip" },
    { path: "/factory/finishing", key: "nav_finishing" },
    { path: "/factory/stock", key: "nav_stock_on_hand" },
    { path: "/factory/despatch", key: "nav_despatch" },
    { path: "/factory/grn", key: "nav_grn" },
    { path: "/coordinator/job-work", key: "nav_jw_register" },
    { path: "/hr/attendance", key: "nav_hr_attendance" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/admin/csv-upload", key: "nav_csv_upload" },
    { path: "/help", key: "nav_help" },
  ],
  qc_officer: [
    { path: "/factory/wip", key: "nav_factory_wip" },
    { path: "/help", key: "nav_help" },
  ],
  wash_master: [
    { path: "/factory/finishing", key: "nav_finishing" },
    { path: "/help", key: "nav_help" },
  ],
  finisher: [
    { path: "/factory/finishing", key: "nav_finishing" },
    { path: "/help", key: "nav_help" },
  ],
  stores_coordinator: [
    { path: "/factory/grn", key: "nav_grn" },
    { path: "/factory/wip", key: "nav_factory_wip" },
    { path: "/factory/stock", key: "nav_stock_on_hand" },
    { path: "/factory/despatch", key: "nav_despatch" },
    { path: "/coordinator/job-work", key: "nav_jw_register" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/admin/csv-upload", key: "nav_csv_upload" },
    { path: "/help", key: "nav_help" },
  ],
  vendor_mg: [
    { path: "/mg/dashboard", key: "nav_mg_dashboard" },
    { path: "/mg/pieces", key: "nav_mg_pieces" },
    { path: "/mg/yarn-po", key: "nav_mg_yarn_po" },
    { path: "/mg/inspections", key: "nav_mg_inspections" },
    { path: "/mg/yarn", key: "nav_mg_yarn" },
    { path: "/help", key: "nav_help" },
  ],
  ric_staff: [
    { path: "/ric/dashboard", key: "nav_ric_dashboard" },
    { path: "/ric/receive", key: "nav_ric_receive" },
    { path: "/ric/transfer", key: "nav_ric_transfer" },
    { path: "/ric/po-queue", key: "nav_ric_po_queue" },
    { path: "/ric/weaver-sale", key: "nav_ric_sale" },
    { path: "/ric/count", key: "nav_ric_count" },
    { path: "/ric/stock", key: "nav_ric_stock" },
    { path: "/help", key: "nav_help" },
  ],
  ric_supervisor: [
    { path: "/ric/dashboard", key: "nav_ric_dashboard" },
    { path: "/ric/po-queue", key: "nav_ric_po_queue" },
    { path: "/ric/stock", key: "nav_ric_stock" },
    { path: "/ric/discrepancies", key: "nav_ric_disc" },
    { path: "/ric/count", key: "nav_ric_count" },
    { path: "/ric/receive", key: "nav_ric_receive" },
    { path: "/help", key: "nav_help" },
  ],
  security_guard: [
    { path: "/guard/today", key: "nav_guard_today" },
    { path: "/gate/visitors", key: "nav_visitors" },
    { path: "/gate/register", key: "nav_gate_register" },
    { path: "/hr/leaves", key: "nav_hr_leaves" },
    { path: "/help", key: "nav_help" },
  ],
};

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setNotes(r.data.filter((n) => !n.read).slice(0, 8))).catch(() => {});
    const iv = setInterval(() => {
      if (navigator.onLine) api.get("/notifications").then((r) => setNotes(r.data.filter((n) => !n.read).slice(0, 8))).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [user, loc.pathname]);

  useEffect(() => setMenuOpen(false), [loc.pathname]);

  if (!user) return children;

  const items = NAV_BY_ROLE[user.role] || [];
  const roleKey = `role_${user.role}`;

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-shell">
      <header className="hag-surface" style={{ borderRadius: 0, borderTop: 0, borderLeft: 0, borderRight: 0 }}>
        <div className="max-w-[1400px] mx-auto flex items-center gap-3 px-4 py-3">
          <button
            data-testid="menu-toggle-btn"
            aria-label="Menu"
            onClick={() => setMenuOpen((m) => !m)}
            className="md:hidden tap-target flex items-center justify-center rounded-md border border-[#1B3A5C]/10 hover:bg-[#FAF6F0]"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--brand-navy)" }}>
              <span className="text-[#C8A86B] font-bold text-lg">H</span>
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm md:text-base truncate" style={{ color: "var(--brand-navy)" }}>{t("app_name")}</div>
              <div className="text-[11px] text-[#7C8E9E] truncate">{t(roleKey)} · {user.name}</div>
            </div>
          </div>
          {!online && (
            <span data-testid="offline-indicator" className="status-chip pace-amber flex items-center gap-1">
              <WifiOff size={12} /> Offline
            </span>
          )}
          <button
            data-testid="lang-toggle-btn"
            onClick={toggle}
            className="tap-target px-3 rounded-md border border-[#1B3A5C]/10 hover:bg-[#FAF6F0] flex items-center gap-1 text-sm"
            title={t("language")}
          >
            <Languages size={16} />
            <span className="hidden sm:inline">{lang === "en" ? "हिन्दी" : "EN"}</span>
          </button>
          <div className="relative">
            <button
              data-testid="notifications-btn"
              className="tap-target flex items-center justify-center rounded-md border border-[#1B3A5C]/10 hover:bg-[#FAF6F0]"
              onClick={() => nav("/notifications")}
              aria-label="Notifications"
            >
              <Bell size={18} />
              {notes.length > 0 && (
                <span
                  data-testid="notif-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-600 text-white flex items-center justify-center"
                >
                  {notes.length}
                </span>
              )}
            </button>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            className="tap-target hidden sm:flex items-center gap-1 px-3 rounded-md border border-[#1B3A5C]/10 hover:bg-[#FAF6F0] text-sm"
          >
            <LogOut size={16} /> {t("logout")}
          </button>
        </div>
      </header>

      <div className="flex-1 flex max-w-[1400px] w-full mx-auto">
        <nav
          className={`${menuOpen ? "block" : "hidden"} md:block md:w-64 flex-shrink-0 hag-surface md:m-3 md:rounded-lg`}
          style={{ borderRadius: menuOpen ? 0 : undefined }}
          data-testid="side-nav"
        >
          <div className="p-3 space-y-1">
            {items.map((it) => {
              const active = loc.pathname.startsWith(it.path);
              return (
                <Link
                  key={it.path}
                  to={it.path}
                  data-testid={`nav-${it.key}`}
                  className={`block px-3 py-3 rounded-md text-sm font-medium ${active ? "bg-[#1B3A5C] text-white" : "text-[#1B3A5C] hover:bg-[#FAF6F0]"}`}
                  style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
                >
                  {t(it.key)}
                </Link>
              );
            })}
            <button
              data-testid="side-logout"
              onClick={() => { logout(); nav("/login"); }}
              className="w-full md:hidden mt-3 px-3 py-3 rounded-md text-sm font-medium border border-[#1B3A5C]/10 flex items-center gap-2"
            >
              <LogOut size={16} /> {t("logout")}
            </button>
          </div>
        </nav>
        <main className="flex-1 p-4 md:p-6 min-w-0">
          <MyMissingDataBanner />
          {children}
        </main>
      </div>
    </div>
  );
}
